import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Heart, Check, Camera, PenLine, Compass, UserPlus, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";
import { confetti } from "@/lib/celebrate";

const STEPS = [
  { id: "picture", icon: Camera, title: "Profilbild hochladen", text: "Damit dich alle erkennen", to: "/profil" },
  { id: "post", icon: PenLine, title: "Ersten Beitrag schreiben", text: "Sag kurz Hallo in die Runde", to: "/feed" },
  { id: "follow", icon: Compass, title: "Mitgliedern folgen", text: "Entdecke Menschen & Beiträge", to: "/community" },
];

function Thanks({ inviter, done, onDone }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (done) return <p className="text-center text-sm font-bold text-emerald-600 inline-flex items-center justify-center gap-1.5 w-full" data-testid="invite-welcome-thanked"><Check className="w-4 h-4" /> Danke geschickt – {inviter.name.split(" ")[0]} freut sich!</p>;
  const send = async () => {
    setBusy(true);
    try { await api.post("/growth/welcome/thanks", { message: msg }); onDone(); toast.success("Dein Dankeschön ist unterwegs 💖"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 p-1.5 pl-4 rounded-full bg-white border border-pink-200 shadow-sm">
      <input value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 200))} placeholder={`Danke, ${inviter.name.split(" ")[0]}! 💖`} data-testid="invite-welcome-thanks-input" className="flex-1 min-w-0 bg-transparent outline-none text-sm" />
      <button onClick={send} disabled={busy} data-testid="invite-welcome-thanks-send" className="shrink-0 h-10 px-4 rounded-full bg-rose-600 text-white text-sm font-extrabold inline-flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Danke sagen</button>
    </div>
  );
}

function Page({ d, setD, onClose }) {
  const inv = d.inviter;
  const first = inv.name.split(" ")[0];
  const follow = async () => {
    try { await api.post(`/users/${inv.user_id}/follow`); setD({ ...d, following: true, steps: { ...d.steps, follow: true } }); toast.success(`Du folgst jetzt ${first} 💕`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Willkommen" className="fixed inset-0 z-[95] bg-[#fff7f9] overflow-y-auto" data-testid="invite-welcome">
      <div className="menu-hero text-white relative overflow-hidden pt-[calc(env(safe-area-inset-top)+2.5rem)] pb-32 px-6 text-center">
        <span className="absolute -left-16 -top-16 w-56 h-56 rounded-full bg-white/10" aria-hidden />
        <span className="absolute -right-10 bottom-0 w-40 h-40 rounded-full bg-white/10" aria-hidden />
        <p className="relative text-[11px] font-extrabold uppercase tracking-[0.3em] text-white/85 fade-rise">Willkommen in der Community</p>
        <h1 className="hero-plain text-white relative font-display text-3xl sm:text-4xl font-black leading-tight mt-2 fade-rise" data-testid="invite-welcome-title">{first} hat dich eingeladen 💖</h1>
        <p className="relative text-white/90 mt-2 max-w-sm mx-auto fade-rise">Schön, dass du da bist! {first} freut sich, dich hier zu sehen.</p>
      </div>
      <div className="max-w-md mx-auto px-5 -mt-16 pb-10 space-y-5 relative">
        <section className="bg-white rounded-[28px] p-5 shadow-xl shadow-rose-200/60 border border-pink-100 text-center fade-rise" data-testid="invite-welcome-inviter">
          <div className="relative w-24 h-24 mx-auto -mt-16">
            <span className="absolute inset-0 rounded-full bg-rose-400/40 animate-ping" aria-hidden />
            <span className="relative block rounded-full ring-4 ring-white shadow-lg"><UserAvatar userId={inv.user_id} name={inv.name} picture={inv.picture} size="w-24 h-24" text="text-3xl" noStory /></span>
            <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-rose-500 text-white flex items-center justify-center ring-4 ring-white"><Heart className="w-4 h-4" fill="currentColor" /></span>
          </div>
          <p className="font-display text-xl font-extrabold mt-3" data-testid="invite-welcome-inviter-name">{inv.name}</p>
          {inv.bio && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{inv.bio}</p>}
          <p className="text-xs font-bold text-rose-500 mt-1">{inv.friends} {inv.friends === 1 ? "Freund" : "Freunde"} eingeladen</p>
          <div className="mt-4 space-y-3">
            {d.following
              ? <p className="h-11 rounded-full bg-emerald-50 text-emerald-700 font-extrabold text-sm inline-flex items-center justify-center gap-1.5 w-full" data-testid="invite-welcome-following"><Check className="w-4 h-4" /> Du folgst {first}</p>
              : <button onClick={follow} data-testid="invite-welcome-follow" className="w-full h-11 rounded-full bg-slate-900 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><UserPlus className="w-4 h-4" /> {first} folgen</button>}
            <Thanks inviter={inv} done={d.thanked} onDone={() => setD({ ...d, thanked: true })} />
          </div>
        </section>
        <section className="bg-white rounded-[28px] p-5 border border-pink-100 fade-rise" data-testid="invite-welcome-steps">
          <h2 className="font-bold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-500" /> Deine ersten Schritte</h2>
          <ul className="mt-3 space-y-2">
            {STEPS.map((s) => {
              const ok = d.steps[s.id];
              return (
                <li key={s.id}>
                  <Link to={s.to} onClick={onClose} data-testid={`invite-welcome-step-${s.id}`} data-done={ok || undefined} className={`flex items-center gap-3 rounded-2xl px-3.5 py-3 border transition-colors ${ok ? "border-emerald-200 bg-emerald-50" : "border-pink-100 hover:border-rose-300"}`}>
                    <span className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${ok ? "bg-emerald-500 text-white" : "bg-rose-50 text-rose-500"}`}>{ok ? <Check className="w-5 h-5" strokeWidth={3} /> : <s.icon className="w-5 h-5" />}</span>
                    <span className="min-w-0 flex-1"><span className="block font-bold text-sm text-slate-800">{s.title}</span><span className="block text-xs text-slate-500">{s.text}</span></span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
        <button onClick={onClose} data-testid="invite-welcome-start" className="w-full h-12 rounded-full bg-rose-600 text-white font-extrabold shadow-lg shadow-rose-300 active:scale-[0.98] transition-transform">Los geht's 🚀</button>
      </div>
    </div>,
    document.body,
  );
}

export function InviteWelcome() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const due = Boolean(user?.referred_by && !user.invite_welcome_seen && user.created_at && Date.now() - new Date(user.created_at) < 7 * 86400000);
  useEffect(() => {
    if (!due) return;
    api.get("/growth/welcome").then(({ data }) => {
      setD(data);
      setTimeout(() => confetti({ x: innerWidth / 2, y: innerHeight * 0.25, count: 120, spread: 1.6, colors: ["#ff1f4b", "#fff", "#fde68a", "#f9a8d4"] }), 350);
    }).catch(() => {});
  }, [due]);
  if (!d) return null;
  const close = () => { setD(null); api.post("/growth/welcome/seen").catch(() => {}); };
  return <Page d={d} setD={setD} onClose={close} />;
}
